import { Link } from 'react-router-dom';
import { Sparkles, Heart, Award, MapPin } from 'lucide-react';
import Seo from '@/components/Seo';

export default function AboutPage() {
  return (
    <div className="pt-20" data-testid="about-page">
      <Seo title="About Lindy" description={"Meet Lindy — the maker behind Whispering Wristwear. Hand-strung Morse code bracelets from a small studio in Paphos, Cyprus."} path="/about" />
      {/* Hero */}
      <section className="section-padding bg-[#F5F5F0]">
        <div className="container-custom px-6 md:px-12 lg:px-24">
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-caption text-[#84A98C] mb-4">Meet the Maker</p>
            <h1 className="heading-display text-4xl md:text-6xl text-[#354F52] mb-6">
              Hi, I'm <span className="text-[#D4AF37] italic">Lindy</span>
            </h1>
            <p className="font-serif italic text-xl text-[#52796F] leading-relaxed">
              "Every bracelet I make carries a quiet message. A secret only the wearer knows."
            </p>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="section-padding">
        <div className="container-custom px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="relative order-2 lg:order-1">
              <img
                src="/images/lindy-in-studio.jpg"
                alt="Lindy in her sunlit Paphos studio, threading beads onto a Whispering Wristwear bracelet at her wooden workbench"
                className="w-full aspect-[4/5] object-cover rounded-sm shadow-float"
              />
              <div className="absolute -bottom-4 -left-4 bg-[#FAF9F6] p-4 rounded-sm shadow-soft">
                <div className="flex items-center gap-2 text-[#354F52]">
                  <MapPin className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.5} />
                  <span className="text-sm font-medium">Online · Markets around Paphos, Cyprus</span>
                </div>
              </div>
            </div>

            <div className="order-1 lg:order-2 space-y-6 text-[#52796F] leading-relaxed">
              <p>
                Whispering Wristwear began at my kitchen table — a single bracelet,
                threaded for a friend who needed something to hold onto. I encoded
                a word in Morse code that meant the world to her, and watched her
                cry when she figured it out.
              </p>
              <p>
                That moment changed everything. I realised jewellery could be
                more than decoration — it could be a <em className="text-[#D4AF37]">secret language</em> between two
                people. A small, daily reminder of love, strength, or the memory
                of someone we miss.
              </p>
              <p>
                Today, every Whispering Wristwear bracelet is still made by hand,
                with love and care. You'll find me online — and in person at
                local markets around Paphos, Cyprus. I source the beads, sketch
                every design, and translate each word into Morse code one bead
                at a time. Nothing is mass-produced. Nothing is rushed.
              </p>
              <p>
                When you wear one of my bracelets, you're wearing a story. Your
                story.
              </p>
              <p className="font-serif italic text-[#354F52] text-lg pt-2">
                — Lindy
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding bg-[#354F52] text-[#FAF9F6]">
        <div className="container-custom px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-caption text-[#84A98C] mb-4">What I Stand For</p>
            <h2 className="heading-display text-3xl md:text-5xl">
              Slow, intentional, and made to last
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {[
              { icon: Heart, title: 'Handmade with Care', body: "No production lines. Every bracelet is threaded, knotted and quality-checked by me, personally." },
              { icon: Award, title: 'Quality That Endures', body: "Carefully sourced gold and silver coloured beads, and clasps tested to last for years." },
              { icon: Sparkles, title: 'Secretly Meaningful', body: "Your word, your name, your motto — translated into Morse code that only you (and the people you trust) can read." },
            ].map((v) => (
              <div key={v.title} className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#52796F] rounded-full mb-6">
                  <v.icon className="w-7 h-7 text-[#D4AF37]" strokeWidth={1.5} />
                </div>
                <h3 className="font-serif text-xl mb-3">{v.title}</h3>
                <p className="text-[#CAD2C5] leading-relaxed">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding">
        <div className="container-custom px-6 md:px-12 lg:px-24 text-center">
          <h2 className="heading-display text-3xl md:text-4xl text-[#354F52] mb-6">
            Ready to wear your story?
          </h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/products" className="btn-primary" data-testid="about-shop-btn">
              Shop Bracelets
            </Link>
            <Link to="/custom" className="btn-secondary" data-testid="about-custom-btn">
              Design Your Own
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
